/**
 * Orphan Bills tab — workflow: orphan_bill_link
 * Self-contained. Delete this folder + the OrphanBillsTab import/tab in BillsPage
 * to fully detach the feature (no impact on the existing Bills tab).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import { Progress } from '../../../components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { RefreshCw, Loader2, Link2, PlusCircle, Check, X, AlertTriangle, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { ResolveDialog } from './ResolveDialog';

const fmtMoney = (v) => {
  const n = parseFloat(String(v ?? '').replace(/[$,]/g, ''));
  return isNaN(n) ? '—' : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ScanProgress = ({ job }) => {
  const pct = job.total ? Math.round((job.processed / job.total) * 100) : 0;
  const s = job.stats || {};
  return (
    <div className="border rounded-lg p-3 space-y-2 bg-muted/20" data-testid="obl-scan-progress">
      <div className="flex items-center justify-between text-xs">
        <span className="flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Scanning bills & parsed documents…</span>
        <span className="font-mono">{job.processed || 0} / {job.total || '…'}</span>
      </div>
      <Progress value={pct} className="h-1.5" />
      <div className="text-[11px] text-muted-foreground">{s.auto_linked || 0} auto-linked · {s.fyi_pending || 0} to review · {s.orphan || 0} orphan · {s.skipped || 0} already handled</div>
    </div>
  );
};

const StatusCell = ({ b }) => {
  const o = b.obl || {};
  const missing = (o.unmatched_accounts || []).length;
  return (
    <div className="flex flex-wrap gap-1 items-center">
      {o.status === 'fyi_pending' ? (
        <Badge variant="outline" className="text-[10px] border-amber-400/50 text-amber-600" data-testid={`obl-status-fyi-${b.bill_id}`}>New meter → {o.suggested_account_number}</Badge>
      ) : (
        <Badge variant="outline" className="text-[10px]" data-testid={`obl-status-orphan-${b.bill_id}`}>Orphan</Badge>
      )}
      {b.is_consolidated && <Badge variant="secondary" className="text-[9px]">Consolidated</Badge>}
      {o.collective_container_id && <Badge variant="secondary" className="text-[9px]">Collective</Badge>}
      {o.line_count > 1 && <span className="text-[10px] text-muted-foreground" data-testid={`obl-missing-${b.bill_id}`}>{missing} of {o.line_count} accounts missing</span>}
    </div>
  );
};

const Row = ({ b, busy, onAct, onResolve }) => (
  <TableRow data-testid={`obl-row-${b.bill_id}`}>
    <TableCell className="text-xs">
      <div className="font-medium flex items-center gap-1">{b.vendor_name || '—'}
        {b.obl?.source === 'document' && <FileText className="h-3 w-3 text-muted-foreground" title="Parsed document (Intake Hub)" />}</div>
      <div className="font-mono text-muted-foreground">{b.account_number || '—'}</div>
    </TableCell>
    <TableCell className="text-xs">{[b.primary_service_address, b.primary_service_state].filter(Boolean).join(', ') || '—'}</TableCell>
    <TableCell className="text-xs capitalize">{b.commodity || '—'}</TableCell>
    <TableCell className="text-xs text-right font-mono">{fmtMoney(b.total_amount_due || b.current_bill_amount)}</TableCell>
    <TableCell><StatusCell b={b} /></TableCell>
    <TableCell>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : b.obl?.status === 'fyi_pending' ? (
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-emerald-600 border-emerald-400/50" onClick={() => onAct(b, 'approve')} data-testid={`obl-approve-${b.bill_id}`}><Check className="h-3 w-3" /> Approve</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-red-500 border-red-400/50" onClick={() => onAct(b, 'reject')} data-testid={`obl-reject-${b.bill_id}`}><X className="h-3 w-3" /> Reject</Button>
        </div>
      ) : (
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => onResolve(b, 'link')} data-testid={`obl-link-${b.bill_id}`}><Link2 className="h-3 w-3" /> Link</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => onResolve(b, 'create')} data-testid={`obl-create-${b.bill_id}`}><PlusCircle className="h-3 w-3" /> Create</Button>
        </div>
      )}
    </TableCell>
  </TableRow>
);

export default function OrphanBillsTab({ accountId }) {
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ orphan: 0, fyi_pending: 0, linked: 0, open: 0 });
  const [busyBill, setBusyBill] = useState(null);
  const [resolve, setResolve] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const [list, cnt] = await Promise.all([
        api.get('/orphan-bill-link/list', { params: { account_id: accountId, status: 'open', limit: 200 } }),
        api.get('/orphan-bill-link/counts', { params: { account_id: accountId } }),
      ]);
      setRows(list.data.bills || []);
      setTotal(list.data.total || 0);
      setCounts(cnt.data || {});
    } catch { toast.error('Failed to load orphan bills'); }
    finally { setLoading(false); }
  }, [accountId]);

  const poll = useCallback(async (announce) => {
    clearTimeout(timer.current);
    try {
      const { data } = await api.get('/orphan-bill-link/scan/status', { params: { account_id: accountId } });
      if (data.status === 'running') { setJob(data); timer.current = setTimeout(() => poll(true), 2000); return; }
      setJob(null);
      if (announce && data.status === 'done') {
        const s = data.stats || {};
        toast.success(`Scanned ${s.scanned || 0} · ${s.auto_linked || 0} auto-linked · ${s.fyi_pending || 0} to review · ${s.orphan || 0} orphan`);
        load();
      } else if (announce && data.status === 'failed') toast.error(`Scan failed: ${data.error || 'unknown error'}`);
    } catch { setJob(null); }
  }, [accountId, load]);

  useEffect(() => { load(); poll(false); return () => clearTimeout(timer.current); }, [load, poll]);

  const runScan = async () => {
    try {
      const { data } = await api.post('/orphan-bill-link/scan', null, { params: { account_id: accountId } });
      setJob(data); timer.current = setTimeout(() => poll(true), 1500);
    } catch (e) { toast.error(e.response?.data?.detail || 'Scan failed to start'); }
  };

  const act = async (bill, action) => {
    setBusyBill(bill.bill_id);
    try {
      await api.post(`/orphan-bill-link/bills/${bill.bill_id}/${action}`, null, { params: { account_id: accountId } });
      toast.success(action === 'approve' ? 'Approved — bill linked' : 'Rejected — kept as orphan');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusyBill(null); }
  };

  return (
    <div className="space-y-4" data-testid="orphan-bills-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm">
          <Badge variant="outline" className="gap-1" data-testid="obl-count-orphan"><AlertTriangle className="h-3 w-3" /> {counts.orphan} orphan</Badge>
          <Badge variant="outline" className="gap-1 border-amber-400/50 text-amber-600" data-testid="obl-count-fyi">{counts.fyi_pending} to review</Badge>
          <span className="text-xs text-muted-foreground" data-testid="obl-count-linked">{counts.linked} linked</span>
        </div>
        <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={runScan} disabled={!!job} data-testid="obl-scan-btn">
          <RefreshCw className={`h-3.5 w-3.5 ${job ? 'animate-spin' : ''}`} />
          {job ? 'Scanning…' : 'Scan bills'}
        </Button>
      </div>
      {job && <ScanProgress job={job} />}
      <div className="border rounded-lg overflow-hidden bg-card">
        {loading ? (
          <div className="space-y-3 p-4">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground" data-testid="obl-empty">
            <Check className="h-10 w-10 mb-3 opacity-40" />
            <p className="text-sm">No orphan bills to review.</p>
            <p className="text-xs mt-1">Run <strong>Scan bills</strong> to check bills and parsed documents that can’t roll up to an account.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="obl-table">
              <TableHeader>
                <TableRow className="bg-muted/30">
                  {['Vendor / Account', 'Service Address', 'Commodity'].map(h => <TableHead key={h} className="text-[10px] font-semibold uppercase">{h}</TableHead>)}
                  <TableHead className="text-[10px] font-semibold uppercase text-right">Amount</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase">Status</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase w-[200px]">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(b => <Row key={b.bill_id} b={b} busy={busyBill === b.bill_id} onAct={act} onResolve={(bill, mode) => setResolve({ bill, mode })} />)}
              </TableBody>
            </Table>
            {total > rows.length && <p className="text-[11px] text-muted-foreground px-4 py-2" data-testid="obl-showing">Showing {rows.length} of {total} — resolve these to see the rest.</p>}
          </div>
        )}
      </div>
      <ResolveDialog open={!!resolve} onOpenChange={(o) => { if (!o) setResolve(null); }} accountId={accountId}
        bill={resolve?.bill} initialMode={resolve?.mode} onDone={load} />
    </div>
  );
}
